<template>
  <div class="__host__ wt2-text-n">
    <div v-if="showFavorite" class="-favorite wt2-text-n">
      <wt-asset-favorite
        :isFavorite="!!favoriteOwner"
        :asset="workModel"
        @favorite="toggleFavorite"
      />
    </div>

    <div
      ref="elTitle"
      class="wt2-text-l2 wt-asset-title"
      :title="title"
      v-text="title"
      :style="titleStyle"
    ></div>
    <div v-if="metadata.socialUserScreenName" class="wt2-text-n -section-title">
      @{{ metadata.socialUserScreenName }}
    </div>
    <div v-if="isKlipy" class="-via-klipy">via KLIPY</div>
    <div class="wt-asset-date">
      {{ (metadata.publicationDate || metadata.creationDate) | printDateTime2 }}
      <div v-if="social.isRelevant" class="-social-divider" />
      <span
        v-if="social.retweet.count"
        class="-social-metric"
        :title="social.retweet.title"
      >
        <i class="wticon wticon-retweet wt2-text-m" />
        <span v-text="social.retweet.label" />
      </span>
      <span
        v-if="social.favorite.count"
        class="-social-metric"
        :title="social.favorite.title"
      >
        <i class="wticon wticon-heart wt2-text-m" />
        <span v-text="social.favorite.label" />
      </span>
      <span
        v-if="social.likes.count"
        class="-social-metric"
        :title="social.likes.title"
      >
        <i class="wticon wticon-heart wt2-text-m" />
        <span v-text="social.likes.label" />
      </span>
    </div>

    <infobar-toolbar />

    <div v-if="isRelevantForTranscribe">
      <div class="-section-title">
        <span class="wt2-text-n2">Metadata</span>
        <span class="-btn-toggle-more wt2-color-link" @click="toggleMetadata">
          <span v-if="showMetadata">Hide</span>
          <span v-else>Show</span>
        </span>
      </div>
    </div>

    <div v-if="description && showMetadataSections" class="-description">
      <div class="-section-title">
        <div class="wt2-text-n2">Description</div>
        <div
          v-if="!isRelevantForTranscribe && isDescriptionTruncated"
          class="-show-entire-description"
        >
          <span
            v-if="showDescriptionClicked"
            @click="showDescriptionClicked = false"
            >Show less</span
          >
          <span v-else @click="showDescriptionClicked = true">Show all</span>
        </div>
      </div>
      <div
        v-if="captionText || galleryRestrictions"
        ref="elDescription"
        class="wt-asset-description"
        :class="{ '-truncated': isDescriptionTruncated }"
        :style="descriptionStyle"
        v-text="captionText || galleryRestrictions"
      />
      <div
        v-if="captionText && galleryRestrictions"
        class="wt-asset-description"
        v-text="galleryRestrictions"
      />
    </div>

    <div
      v-if="showRestrictions && showMetadataSections"
      class="wt-asset-restrictions"
    >
      <template v-if="isContainsRestrictions(this.asset)">
        <div class="wt2-text-n2 -section-title">Restrictions</div>
        <span class="wt-asset-restrictions-text">
          <i
            class="wticon wticon-Restriction_v8"
            :class="{ '-restricted': !isDynamic(this.asset) }"
          ></i>
          <span class="wt-aseet-restrictions-content">{{
            getRestrictions
          }}</span>
        </span>
      </template>
      <template v-else-if="isFairUse(this.asset)">
        <div class="wt2-text-n2 -section-title">3rd party content</div>
        <span class="wt-asset-restrictions-text">
          <i class="wticon wticon-Restriction_v8"></i>
          <span class="wt-aseet-restrictions-content">{{
            getRestrictions
          }}</span>
        </span>
      </template>
      <template v-else-if="isEditorialOrCommercialRestrictionsOnly">
        <div class="wt2-text-n2 -section-title">Restrictions</div>
        <span class="wt-asset-restrictions-text">
          <i class="wticon wticon-Restriction_v8"></i>
          <span v-if="isEditorial" class="wt-aseet-restrictions-content">
            {{ 'generic.FOR_EDITORIAL_USE' | culture }}
          </span>
          <span v-if="isCommercial" class="wt-aseet-restrictions-content">
            {{ 'generic.FOR_COMMERCIAL_USE' | culture }}
          </span>
        </span>
      </template>
    </div>

    <asset-preview-video-transcript-controls
      class="-transcribe-container"
      :asset="$props.asset"
    />

    <div
      v-if="transcript && showMetadataSections"
      class="-transcript-container"
    >
      <div class="-section-title">
        <span class="wt2-text-n2">Transcript from Asset Provider</span>
        <span
          v-if="!isRelevantForTranscribe"
          class="-btn-toggle-more wt2-color-link"
          @click="toggleProviderTranscript"
        >
          <span v-if="showProviderTranscript">Hide</span>
          <span v-else>Show</span>
        </span>
      </div>
      <div
        v-if="showProviderTranscript"
        class="-transcript-body"
        v-text="transcript"
      />
    </div>

    <div v-if="showMetadataSections" class="-more-info-container wt2-text-n">
      <div class="-section-title">
        <span class="wt2-text-n2">More Info</span>
        <span
          v-if="!isRelevantForTranscribe"
          class="-btn-toggle-more wt2-color-link"
          @click="toggleShowMoreInfo"
        >
          <span v-if="showMoreInfo">Hide</span>
          <span v-else>Show</span>
        </span>
      </div>
      <div v-if="showMoreInfo" class="-more-info-body">
        <div>Asset ID: {{ metadata.assetUUID }}</div>
        <div>Source: {{ providerDisplayText }}</div>
        <div v-if="metadata.copyrights">
          Copyrights: {{ metadata.copyrights }}
        </div>
        <div v-if="asset.location">Location: {{ asset.location }}</div>
        <div v-if="metadata.keywords">
          Keywords:
          <span v-if="metadata.keywords">
            <span v-for="(keyword, i) in metadata.keywords" :key="i" class="tag"
              >{{ keyword }},
            </span>
          </span>
        </div>
      </div>
    </div>

    <template>
      <div v-if="$slots.transcription" class="wrapper">
        <div class="-section-title -transcript-header">
          <span class="wt2-text-n2">Transcript</span>
          <span
            class="-btn-toggle-more wt2-color-link"
            @click="toggleShowTranscription"
          >
            <span v-if="showTranscript">Hide</span>
            <span v-else>Show</span>
          </span>
        </div>
        <slot name="transcription" v-if="showTranscript" />
      </div>
    </template>

    <div v-if="canEdit || canDelete" class="-asset-actions">
      <div v-if="canEdit" class="-edit" @click.stop.prevent="$_clickEditAsset">
        <i class="wticon wticon-edit-04" />
        <span>Edit info</span>
      </div>
      <div
        v-if="canDelete"
        class="-delete"
        @click.stop.prevent="$_clickDeleteAsset"
      >
        <i class="wticon wticon-trash2" />
        <span>Delete</span>
      </div>
    </div>
  </div>
</template>

<script>
import app from 'app';
import Vue from 'vue';
import AssetMixins from 'components/assets/mixins/AssetMixins.vue';
import InfobarToolbar from './InfobarToolbar.vue';
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex';
import AssetPreviewVideoTranscriptControls from 'v/AssetPreview/AssetPreviewVideoTranscriptControls.vue';
import AssetFavorite from 'components/assets/mixins/AssetFavorite.vue';
import { isAssetCandidateForTranscribe } from 'v/Story/Timeline/api/transcription';

export default Vue.extend({
  mixins: [AssetMixins],
  components: {
    AssetPreviewVideoTranscriptControls,
    InfobarToolbar,
    'wt-asset-favorite': AssetFavorite,
  },

  props: {
    asset: { type: Object, required: true },
    videoTrimming: { type: Boolean, default: false },
  },

  data() {
    return {
      titleLineHeight: 0,
      descriptionLineHeight: 0,
      showDescriptionClicked: false,
      isDescriptionTruncated: false,
      restrictions: {},
      showProviderTranscript: false,
      showMetadata: false,
      showTranscript: false,
    };
  },

  computed: {
    ...mapGetters('assetEditor', ['workModel']),
    ...mapState('assetEditor', ['openedFrom', 'showMoreInfo']),
    ...mapState({
      isOpenedFromFavorites: (state) => {
        return (
          state?.assetGallery?.searchContextId === app.PROVIDERS.FAVORITES.id
        );
      },
    }),

    showMetadataSections() {
      return (
        !this.isRelevantForTranscribe ||
        (this.isRelevantForTranscribe && this.showMetadata)
      );
    },

    isAssetCandidateForTranscribe() {
      return isAssetCandidateForTranscribe(this.asset);
    },

    isRelevantForTranscribe() {
      return this.videoTrimming && this.isAssetCandidateForTranscribe;
    },

    favoriteOwner() {
      return this.workModel.metadata.favoriteOwner;
    },

    showFavorite() {
      return this.openedFrom === 'asset-gallery';
    },

    title() {
      let rv = this.metadata.title;
      if (this.isSocialImageOrVideo) {
        if (this.isFACEBOOK) {
          rv = this.metadata.socialUserName;
        } else if (this.isTWITTER || this.isINSTAGRAM) {
          rv = `${this.metadata.socialUserName}`;
        }
      } else if (this.isGIF) {
        rv = this.metadata.caption;
      }

      return rv;
    },

    captionText() {
      return this.isGIF ? '' : this.metadata.caption ?? '';
    },

    galleryRestrictions() {
      return this.isInMyGallery && !this.isGIF ? this.getRestrictions : '';
    },

    description() {
      return [this.captionText, this.galleryRestrictions]
        .filter((text) => text && text.trim())
        .join('\n');
    },

    showRestrictions() {
      if (this.isInMyGallery) {
        // show restriction for uploaded in description;
        return false;
      } else {
        return !_.isEmpty(this.asset._restrictions);
      }
    },

    metadata() {
      return this.asset.metadata;
    },

    isKlipy() {
      return this.metadata.providerId?.toLowerCase() === 'klipy';
    },

    transcript() {
      return this.metadata.transcript;
    },

    providerDisplayText() {
      const id = this.metadata.providerId.toLowerCase();
      if (id === 'asset_server') {
        return 'Custom';
      } else if (id === 'twitter') {
        return 'Twitter';
      } else if (id === 'facebook') {
        return 'Facebook';
      } else if (id === 'instagram') {
        return 'Instagram';
      } else if (id === 'tenor') {
        return 'Tenor';
      } else if (id === 'klipy') {
        return 'KLIPY';
      } else if (id === 'kaltura') {
        return 'My Library';
      }
      const provider = _.find(tk.context.allProviders, {
        id: this.asset.metadata.providerId,
      });
      return provider ? provider.displayText : 'Not Available';
    },

    showDescription() {
      return !this.isGIF && this.title !== this.description;
    },

    descriptionStyle() {
      if (this.showDescriptionClicked || !this.descriptionLineHeight) {
        return {};
      }
      return {
        '-webkit-line-clamp': 4,
        'max-height': 4 * this.descriptionLineHeight + 'px',
      };
    },

    titleStyle() {
      return {
        '-webkit-line-clamp': 4,
        'max-height': 4 * this.titleLineHeight + 'px',
      };
    },

    social() {
      const favoriteCount = this.metadata.socialFavoriteCount ?? 0;
      const favoriteTitle =
        favoriteCount && `${favoriteCount.toLocaleString()} Likes`;
      const retweetCount = this.metadata.socialRetweetCount ?? 0;
      const retweetTitle =
        retweetCount && `${retweetCount.toLocaleString()} Retweets`;
      const likesCount =
        this.metadata.instagramAssetMetadata?.socialLikesCount ?? 0;
      const likesTitle = likesCount && `${likesCount.toLocaleString()} Likes`;

      return {
        isRelevant: favoriteCount || retweetCount || likesCount,
        favorite: {
          count: favoriteCount,
          label: favoriteCount.toFriendlyNumber(1),
          title: favoriteTitle,
        },
        retweet: {
          count: retweetCount,
          label: retweetCount.toFriendlyNumber(1),
          title: retweetTitle,
        },
        likes: {
          count: likesCount,
          label: likesCount.toFriendlyNumber(1),
          title: likesTitle,
        },
      };
    },

    canEdit() {
      return (
        !this.isINFOGRX &&
        (app.assetType.isAssetFromEditableProvider(this.asset) ||
          this.isInMyGallery)
      );
    },

    canDelete() {
      return (
        !this.isOpenedFromFavorites &&
        this.openedFrom === 'asset-gallery' &&
        (this.isINFOGRX || this.isInMyGallery)
      );
    },
  },

  mounted() {
    const showTranscriptionStashed = app.remoteStash.get(
      'InfobarShowTranscription'
    );
    this.showTranscript =
      showTranscriptionStashed === undefined ||
      showTranscriptionStashed === true;

    // const showMetadataStashed = app.remoteStash.get('InfobarShowMetadata');
    // this.showMetadata = showMetadataStashed === true;

    this.$_updateInfoContainers();
  },

  updated() {
    this.$_updateInfoContainers();
  },

  methods: {
    ...mapActions([
      'SELECTED_ASSET_METADATA_EDITED',
      'GALLERY_ASSET_METADATA_EDITED',
      'TOGGLE_FAVORITE_ASSET',
    ]),
    ...mapMutations('assetEditor', ['toggleShowMoreInfo']),

    toggleFavorite() {
      this.TOGGLE_FAVORITE_ASSET({ asset: this.workModel });
    },

    toggleShowTranscription() {
      this.showTranscript = !this.showTranscript;
      app.remoteStash.set('InfobarShowTranscription', this.showTranscript);
    },

    $_updateInfoContainers() {
      this.$nextTick(() => {
        if (this.isRelevantForTranscribe) {
          this.showDescriptionClicked = true;
          // this.showTranscript = true;
          if (!this.showMoreInfo) {
            this.toggleShowMoreInfo();
          }
        }
        if (this.$refs.elDescription) {
          const el = this.$refs.elDescription;
          const cs = getComputedStyle(el);
          this.descriptionLineHeight = parseInt(cs.lineHeight);
          if (el.scrollHeight > el.clientHeight) {
            this.isDescriptionTruncated = true;
          }
        }

        if (this.$refs.elTitle) {
          const el = this.$refs.elTitle;
          const cs = getComputedStyle(el);
          this.titleLineHeight = parseInt(cs.lineHeight);
        }
      });
    },

    toggleMetadata() {
      this.showMetadata = !this.showMetadata;
      // app.remoteStash.set('InfobarShowMetadata', this.showMetadata);
    },

    toggleProviderTranscript() {
      this.showProviderTranscript = !this.showProviderTranscript;
    },

    async setRestrictionsTooltip() {
      await this.$nextTick();
      const $restrictions = $(this.$el).find('.wt-asset-restrictions');
      if (
        !$.isElement($restrictions) ||
        $restrictions[0].scrollHeight <= $restrictions[0].offsetHeight
      ) {
        return;
      }
      const tooltipOptions = {
        placement: 'left',
        delay: 750,
        trigger: 'hover',
        container: this.$el.parentElement,
        template:
          '<div class="tooltip wt-preset-tooltip" role="tooltip"><div class="tooltip-arrow"></div><div class="tooltip-inner"></div></div>',
      };

      $restrictions.tooltip(
        _.extend(tooltipOptions, {
          html: true,
          title: this.getRestrictions
            ? this.getRestrictions
            : app.culture.generic.FAIR_USE_MESSAGE,
        })
      );
    },

    $_clickEditAsset: _.debounce(
      function () {
        if (this.isPreparing || this.isPreparingFailed) {
          return;
        }

        this.$root.$emit('pause-video');
        this.mixinShowViewInModal({
          type: 'normal',
          component: 'wt-edit-asset-details',
          componentPropsData: {
            asset: this.asset,
          },
          closeOnEscape: true,
          closeOnClickOut: false,
        }).$on('close', (e) => {
          if (e && e.metadata) {
            Vue.set(
              this.asset,
              'metadata',
              Object.assign({}, this.asset.metadata, e.metadata)
            );
            const updateOptions = {
              metadata: e.metadata,
              assetUUID: e.assetUUID,
            };
            if (this.openedFrom === 'asset-gallery') {
              this.GALLERY_ASSET_METADATA_EDITED(updateOptions);
            }
            this.SELECTED_ASSET_METADATA_EDITED(updateOptions);
            app.trigger('app.asset.edited', updateOptions);
          }
        });
      },
      app.TIME.MENTAL.λ,
      { leading: true, trailing: false }
    ),

    $_clickDeleteAsset: _.debounce(
      function () {
        this.$root.$emit('pause-video');
        app.namespace.Controller.AssetController.deletePrivatelyUploadedAsset(
          this.asset
        ).then(() => {
          this.$emit('close');
        });
      },
      app.TIME.MENTAL.λ,
      { leading: true, trailing: false }
    ),
  },
});
</script>
<style lang="scss" scoped>
.-action {
  @include favoriteIcon();
}
.-favorite {
  cursor: pointer;
  top: 25px;
  position: absolute;
  right: 15px;
  .-action {
    cursor: pointer;
    position: relative;
    height: 20px;
    margin: 0;
    top: 0;
    right: 0;

    &:after {
      font-size: 20px;
    }

    /deep/.wt2-google-like-loader.-default.-progress {
      left: -1px;
      top: 4px;
    }
  }
}

.-section-title {
  margin-bottom: 10px;
  user-select: none;
}

.-transcript-header {
  position: sticky;
  top: 0;
  background-color: #f7f7f7;
  z-index: 1;
  padding: 10px 0 0 0;
  margin: 0;
  height: 35px;
}

.-show-entire-description {
  display: flex;
  justify-content: flex-end;
  margin-top: 2px;
  > span {
    cursor: pointer;
    color: $color-primary;
    &:hover {
      color: $color-blue-d;
    }
  }
}

.-transcript-container {
  margin-bottom: 20px;

  .-section-title {
    margin-bottom: 0;
  }
  .-transcript-body {
    margin-top: 20px;
    word-break: break-word;
  }
}

.-transcribe-container {
  padding-bottom: 20px;
}

.-section-title {
  display: flex;
  justify-content: space-between;
}

.-btn-toggle-more {
  cursor: pointer;
  user-select: none;
}

.-more-info-container {
  .-more-info-body {
    word-break: break-word;
    > div {
      margin-bottom: 10px;
    }
  }
}

.__host__ {
  padding: 30px 57px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;
  > div {
    flex-shrink: 0;
  }

  &.wt-inside-asset-editor {
    flex: none;
    padding-top: 0;
  }

  .wt-textoverlay-tab & {
    padding-top: 0;
  }

  .wt-mark-this-asset-button {
    float: left;
    width: 25px;
    height: 25px;
    border-radius: 100%;
    text-align: center;
    border: 1px solid #4a4a4a;
    color: #4a4a4a;
    margin: -1px 10px 0 0;
    cursor: pointer;
    background-color: transparent;

    .wticon {
      margin: 0;
      padding: 0 3px;
    }
  }

  .wt-asset-title {
    line-height: 20px;
    font-size: 16px;
    margin: 0 0 10px 0;
  }

  .wt-asset-description,
  .wt-asset-restrictions {
    .webpagemetaasset & {
      word-break: break-all;
    }
  }

  .-description {
    &.-truncated {
      margin-bottom: 0;
    }
  }

  .wt-asset-restrictions {
    margin-bottom: 20px;
    .wt-asset-restrictions-text {
      i {
        background-color: #4c4c4c;
        color: white;
        display: inline-block;
        border-radius: 50%;
        font-size: 16px;
        margin: 0 5px 3px 0;

        &.-restricted {
          background-color: #ff3166;
        }
      }
    }
  }

  .-via-klipy {
    font-size: 12px;
    margin-bottom: 20px;
  }

  .wt-asset-date {
    font-size: 12px;
    margin-bottom: 20px;

    .-social-divider {
      display: inline;
      width: 0;
      height: 17px;
      border-right: 1px solid $color-black-l;
      margin: 0 15px;
    }
    .-social-metric {
      display: inline-flex;
      align-items: baseline;
      margin-right: 20px;
      .wticon {
        align-self: flex-start;
        margin-right: 5px;
      }
    }
  }

  .wt-infobar-toolbar {
    margin-bottom: 30px;
  }

  .wt-asset-title,
  .wt-asset-description,
  .wt-asset-restrictions {
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    flex-direction: column;
  }

  .wt-asset-description,
  .wt-asset-restrictions {
    margin-bottom: 20px;
  }

  .-asset-actions {
    width: 100%;
    padding: 30px 0 30px;
    display: flex;
    justify-content: space-between;
    flex-grow: 1;
    align-items: flex-end;

    > div {
      cursor: pointer;
      font-size: 14px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      &.-edit {
        color: $color-primary;
        &:hover {
          color: $color-blue-d;
        }
      }
      &.-delete {
        color: $wt-light-pink;
        &:hover {
          color: $color-pink-d;
        }
      }

      .wticon {
        font-size: 16px;
        margin-right: 5px;
      }
    }
  }

  .wt-twitter-more-info {
    font-size: 11px;
    padding-top: 2px;
    color: #565a5c;
    margin-bottom: 18px;

    .wticon {
      font-size: 14px;
      padding: 0;
      vertical-align: middle;
      display: inline-block;
    }
    .wt-asset-twitter-info-block {
      display: inline-block;
      font-size: 11px;
      padding-right: 10px;
      vertical-align: middle;
    }
  }

  .wt-asset-toggler-container {
    font-size: 12px;
    margin: 5px 0;

    .wt-asset-toggler {
      display: flex;
      align-items: center;

      .wt-asset-toggler-toggle {
        @include checkBoxSlide(
          26px,
          12px,
          '',
          '',
          'wochit',
          #3f4857,
          #fffffd,
          '',
          #16e2b8,
          #fffffd,
          ''
        );

        label {
          &:after {
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.37);
          }
        }
      }

      .wt-asset-auto-transition-text {
        color: #4a4a4a;
        font-size: 12px;
        font-weight: 400;
        margin-left: 11px;
        margin-top: 2px;
      }
    }
  }
}
</style>
